import get from 'lodash.get';

export function capitalizeFirstLetter(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const Colors = {
  DEFAULT: 'default',
  WHITE: 'white',
  RED: 'red',
  GREEN: 'green',
  BLUE: 'blue',
  SLATE: 'slate',
  LIGHT: 'light',
  AMBER: 'amber',
  YELLOW: 'yellow',
  INDIGO: 'indigo',
  PURPLE: 'purple',
  PINK: 'pink'
};

export const wrapperColorSets = {
  [Colors.WHITE]: {
    border: `border-white`,
    text: `text-white`,
    bg: `bg-white`,
    divider: `divide-white`,
    translation: 'Blanc'
  },
  [Colors.LIGHT]: {
    border: `border-slate-400/10`,
    text: `text-slate-300`,
    bg: `bg-slate-50`,
    divider: `divide-slate-400/10`,
    translation: 'Gris clair'
  },
  [Colors.DEFAULT]: {
    border: `border-slate-200`,
    text: `text-slate-600`,
    bg: `bg-slate-100`,
    divider: `divide-slate-200`,
    translation: 'Défaut'
  },
  [Colors.SLATE]: {
    border: `border-slate-500/10`,
    text: `text-slate-600`,
    bg: `bg-slate-50`,
    divider: `divide-slate-500/10`,
    translation: 'Gris'
  },
  [Colors.RED]: {
    border: `border-red-600/10`,
    text: `text-red-700`,
    bg: `bg-red-50`,
    divider: `divide-slate-600/10`,
    translation: 'Rouge'
  },
  [Colors.YELLOW]: {
    border: `border-yellow-600/20`,
    text: `text-yellow-600`,
    bg: `bg-yellow-50`,
    divider: `divide-yellow-600/20`,
    translation: 'Jaune'
  },
  [Colors.AMBER]: {
    border: `border-amber-600/20`,
    text: `text-amber-600`,
    bg: `bg-amber-50`,
    divider: `divide-amber-600/20`,
    translation: 'Ambré'
  },
  [Colors.GREEN]: {
    border: `border-green-600/20`,
    text: `text-green-700`,
    bg: `bg-green-50`,
    divider: `divide-green-600/20`,
    translation: 'Vert'
  },
  [Colors.BLUE]: {
    border: `border-blue-700/20`,
    text: `text-blue-700`,
    bg: `bg-blue-50`,
    divider: `divide-blue-600/20`,
    translation: 'Bleu'
  },
  [Colors.INDIGO]: {
    border: `border-indigo-700/10`,
    text: `text-indigo-700`,
    bg: `bg-indigo-50`,
    divider: `divide-indigo-600/20`,
    translation: 'Indigo'
  },
  [Colors.PURPLE]: {
    border: `border-purple-700/10`,
    text: `text-purple-700`,
    bg: `bg-purple-50`,
    divider: `divide-purple-600/20`,
    translation: 'Violet'
  },
  [Colors.PINK]: {
    border: `border-pink-700/10`,
    text: `text-pink-700`,
    bg: `bg-pink-50`,
    divider: `divide-pink-600/20`,
    translation: 'Rose'
  }
};

export const inputClasses = `outline-none bg-white rounded py-2 px-5 block w-full appearance-none leading-normal transition duration-300 border border-solid text-slate-600 border-slate-400 focus:border-blue-600 focus:outline-1 focus:outline-blue-600 focus:outline-offset-0 focus:placeholder-slate-600 [&.focused]:border-blue-600 [&.focused]:outline-1 [&.focused]:outline-blue-600 [&.focused]:outline-offset-0 [&.focused]:placeholder-slate-600 placeholder:text-slate-400`;

export const cleanString = str => {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s]/gi, '')
    .toLowerCase();
};

export function normalizeText(text) {
  return text.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

export const onSearch = (items, inputValue, searchDone, clear) => {
  if (inputValue.length >= 1) {
    const sanitizedKeyword = normalizeText(inputValue);
    const matchingItems = items.filter(node => {
      const elementText = normalizeText(node.label);
      return elementText.includes(sanitizedKeyword);
    });

    searchDone(matchingItems);
  } else {
    searchDone(items);
  }
};

export function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function scrollToBottom(ref) {
  if (ref.current) {
    ref.current.scrollTop = ref.current.scrollHeight;
  }
}

export function compressHTML(html) {
  return html
    .replace(/[\r\n\t]+/g, ' ') // Replace line breaks, tabs with a space
    .replace(/<!--.*?-->/g, '') // Remove comments
    .replace(/\s*<\s*/g, '<') // Remove spaces around and inside opening brackets
    .replace(/\s*>\s*/g, '>') // Remove spaces around and inside closing brackets
    .replace(/\s{2,}/g, ' '); // Replace multiple spaces with a single space
}

export const cleanHtml = (html, withSpaces) => {
  if (!html) {
    return '';
  }

  const regex = /<[^>]+>|[\s]/g;
  const replacer = withSpaces ? ' ' : '';
  return String(html).replace(regex, replacer).trim();
};

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
